import type { GridView, SeasonDeleted, TableSettings, UserSettings, WorkItem } from "@api/generated/model";
import {
  getWorkspaceSeasonsQueryKey,
  getWorkspaceSettingsQueryKey,
  useWorkspaceCreateSeason,
  useWorkspaceDeleteSeason,
  useWorkspaceSaveSetting,
  useWorkspaceSeasons,
  useWorkspaceSettings,
} from "@api/generated/workspace/workspace";
import { useDebouncedCallback, useDebouncedValue } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type { WorkItem };
/** A work item as a screen describes it; the time is stamped when it is stored. */
export type NewWorkItem = Omit<WorkItem, "at" | "subtitle"> & { subtitle?: string | null };

const MAX_RECENT = 8;
const LONG = { staleTime: 5 * 60_000 };

/** The user's settings (typed, every field present); undefined while loading. */
export function useSettings(): UserSettings | undefined {
  return useWorkspaceSettings({ query: LONG }).data;
}

/** Store one setting: the screen follows at once (optimistic), the server's answer then replaces it (or a failure
 *  rolls it back), so e.g. switching the season changes every year label immediately. ``then``: this save's own
 *  outcome (a field's "Mentve"). */
export function useSaveSetting() {
  const client = useQueryClient();
  const queryKey = getWorkspaceSettingsQueryKey();
  const { mutate } = useWorkspaceSaveSetting({
    mutation: {
      onMutate: ({ key, data }) => {
        const before = client.getQueryData<UserSettings>(queryKey);
        if (before) client.setQueryData(queryKey, { ...before, [key]: data.value });
        return { before };
      },
      // the field goes back to its old value; the toast says why (once, however many saves failed together)
      meta: { toastId: "settings-save" },
      onError: (_error, _vars, context) => {
        if (context?.before) client.setQueryData(queryKey, context.before);
      },
      onSuccess: (data) => client.setQueryData(queryKey, data),
    },
  });
  return useCallback(
    <K extends keyof UserSettings>(key: K, value: UserSettings[K], then?: Parameters<typeof mutate>[1]) =>
      mutate({ key, data: { value } }, then),
    [mutate],
  );
}

const DRAFT_SAVE_DELAY = 1000;

/** Keep a draft with the user's settings a second after its last change (it follows the user to any browser or
 *  computer). Nothing is stored while the draft equals ``stored``, or — without a stored draft of its own (null) —
 *  the value the page opened with; a change not yet stored when the page is left is stored then. Returns when it
 *  was last saved (this visit), and ``saveNow`` (store a value at once). */
export function useAutosave<K extends keyof UserSettings>(
  key: K,
  value: UserSettings[K],
  stored: UserSettings[K],
): { savedAt: Date | null; saveNow: (value: UserSettings[K]) => void } {
  const save = useSaveSetting();
  const json = useMemo(() => JSON.stringify(value), [value]);
  const [settled] = useDebouncedValue(json, DRAFT_SAVE_DELAY);
  const last = useRef(stored == null ? json : JSON.stringify(stored));
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const latest = useRef(json);
  latest.current = json;
  const saveRef = useRef(save);
  saveRef.current = save;
  const store = useCallback(
    (text: string) => {
      last.current = text;
      saveRef.current(key, JSON.parse(text) as UserSettings[K]);
      setSavedAt(new Date());
    },
    [key],
  );
  useEffect(() => {
    if (settled !== last.current) store(settled);
  }, [settled, store]);
  useEffect(
    () => () => {
      // leaving the page within the wait: the last change is saved, not lost
      if (latest.current !== last.current) store(latest.current);
    },
    [store],
  );
  /** Save at once (a recorded draft reset must not come back if the page is left right away). */
  const saveNow = useCallback((v: UserSettings[K]) => store(JSON.stringify(v)), [store]);
  return { savedAt, saveNow };
}

/** Open a new breeding season (Tenyészév): the seasons list follows at once. */
export function useCreateSeason(onDone?: (ev: number) => void) {
  const client = useQueryClient();
  const create = useWorkspaceCreateSeason({
    mutation: {
      onSuccess: (seasons, { data }) => {
        client.setQueryData(getWorkspaceSeasonsQueryKey(), seasons);
        onDone?.(data.ev);
      },
    },
  });
  return { create: (ev: number) => create.mutate({ data: { ev } }), pending: create.isPending };
}

/** Remove an empty season opened by mistake (administrators): the seasons list and the working season (the server
 *  moves the users who worked in it) follow at once. The screen shows a refusal itself (what still names the year). */
export function useDeleteSeason(onDone?: (result: SeasonDeleted) => void) {
  const client = useQueryClient();
  const remove = useWorkspaceDeleteSeason({
    mutation: {
      meta: { inlineError: true },
      onSuccess: (result) => {
        client.setQueryData(getWorkspaceSeasonsQueryKey(), result.seasons);
        void client.invalidateQueries({ queryKey: getWorkspaceSettingsQueryKey() });
        onDone?.(result);
      },
    },
  });
  return { remove: (ev: number) => remove.mutate({ ev }), pending: remove.isPending, error: remove.error, reset: remove.reset };
}

/** The selected breeding season (Tenyészév): the personal setting, else the latest year that has data. Switching it
 *  also moves a page that carries the year in its URL (?ev=), so its links and pins keep showing the chosen year. */
export function useSeason(): { ev: number | undefined; years: number[]; setEv: (ev: number) => void } {
  const stored = useSettings()?.season;
  const seasons = useWorkspaceSeasons({ query: LONG }).data ?? [];
  const save = useSaveSetting();
  const router = useRouter();
  const years = seasons.map((s) => s.ev);
  const ev = stored != null && years.includes(stored) ? stored : (seasons.find((s) => s.plots > 0)?.ev ?? years[0]);
  const setEv = useCallback(
    (value: number) => {
      save("season", value);
      const search = router.state.location.search as Record<string, unknown>;
      if ("ev" in search) void router.navigate({ to: ".", search: { ...search, ev: value }, replace: true } as never);
    },
    [save, router],
  );
  return { ev, years, setEv };
}

/** The year a page shows: the working season. A link that carries a year (``?ev=``, e.g. a pinned 2023 trial) switches
 *  the working season to it, so the header and every other page agree on one year. */
export function useSeasonFromLink(linked: number | undefined): number | undefined {
  const { ev, years, setEv } = useSeason();
  const wanted = linked !== undefined && years.includes(linked) ? linked : undefined;
  useEffect(() => {
    if (wanted !== undefined && wanted !== ev) setEv(wanted);
  }, [wanted, ev, setEv]);
  return wanted ?? ev;
}

/** The saved column views (Nézet) of one data table. */
export function useGridViews(table: string): { views: GridView[]; save: (view: GridView) => void; remove: (name: string) => void } {
  const all = useSettings()?.grid_views ?? {};
  const saveSetting = useSaveSetting();
  const views = all[table] ?? [];
  return {
    views,
    save: (view) => saveSetting("grid_views", { ...all, [table]: [...views.filter((v) => v.name !== view.name), view] }),
    remove: (name) => saveSetting("grid_views", { ...all, [table]: views.filter((v) => v.name !== name) }),
  };
}

export type { TableSettings };
export const DEFAULT_TABLE: TableSettings = { order: [], hidden: [], sort: [], filters: {}, widths: {}, grid: null };
const TABLE_SAVE_DELAY = 600;

/** How the person last left one table (its sort, filters, column order and hidden columns), on whichever computer they
 *  did. A change shows at once (the settings cache) and is stored a moment later as ``table:<id>``; one not yet stored
 *  when the page is left is stored then. */
/** What a table's ``id`` may be: the server keeps its settings under ``table:<id>`` only then (system TABLE_KEY). */
export const TABLE_ID = /^[a-z][a-z0-9_.:-]{0,79}$/;

export function useTableSettings(id: string): [TableSettings, (next: TableSettings) => void] {
  if (import.meta.env.DEV && !TABLE_ID.test(id))
    throw new Error(`"${id}" is not a table id (${TABLE_ID}): its settings would not be saved`);
  const client = useQueryClient();
  const queryKey = getWorkspaceSettingsQueryKey();
  const stored = useWorkspaceSettings({ query: { ...LONG, select: (s) => s.tables[id] } }).data ?? DEFAULT_TABLE;
  const { mutate } = useWorkspaceSaveSetting({
    // the answer holds the other settings too, newer than the cache when another save came between
    mutation: { meta: { toastId: "settings-save" }, onSuccess: (data) => client.setQueryData(queryKey, data) },
  });
  const store = useDebouncedCallback((value: TableSettings) => mutate({ key: `table:${id}`, data: { value } }), {
    delay: TABLE_SAVE_DELAY,
    flushOnUnmount: true,
  });
  const set = useCallback(
    (next: TableSettings) => {
      const before = client.getQueryData<UserSettings>(queryKey);
      if (!before || JSON.stringify(before.tables[id] ?? DEFAULT_TABLE) === JSON.stringify(next)) return;
      client.setQueryData<UserSettings>(queryKey, { ...before, tables: { ...before.tables, [id]: next } });
      store(next);
    },
    [client, queryKey, id, store],
  );
  return [stored, set];
}

export function usePins() {
  const settings = useSettings();
  const pins = settings?.pins ?? [];
  const client = useQueryClient();
  const mutation = useWorkspaceSaveSetting({
    mutation: {
      meta: { inlineError: true }, // the pin dialog shows it
      onSuccess: (data) => client.setQueryData(getWorkspaceSettingsQueryKey(), data),
    },
  });
  const store = (value: WorkItem[]) => mutation.mutateAsync({ key: "pins", data: { value } });
  return {
    pins,
    ready: !!settings,
    pending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    save: (item: NewWorkItem, previousHref?: string) => {
      const index = pins.findIndex((pin) => pin.href === (previousHref ?? item.href));
      const pin: WorkItem = { subtitle: null, ...item, at: null };
      return store(index < 0 ? [...pins, pin] : pins.map((old, i) => (i === index ? pin : old)));
    },
    remove: (href: string) => store(pins.filter((pin) => pin.href !== href)),
  };
}

/** One recent entry per thing shown on a page: the same line on another tab of its sheet (another href, the same
 *  path) is the same entry; its pedigree tree is another. */
const pathOf = (href: string) => href.split("?")[0];
const sameItem = (a: NewWorkItem, b: NewWorkItem) =>
  a.href === b.href ||
  (a.kind === b.kind && a.title === b.title && (a.subtitle ?? null) === (b.subtitle ?? null) && pathOf(a.href) === pathOf(b.href));

export function useRecent(): { recent: WorkItem[]; push: (item: NewWorkItem) => void } {
  const stored = useSettings()?.recent;
  // entries saved before that rule may repeat: the newest of each stays
  const recent = useMemo(() => (stored ?? []).filter((r, i, all) => all.findIndex((x) => sameItem(x, r)) === i), [stored]);
  const save = useSaveSetting();
  return {
    recent,
    push: (item) => {
      const found = recent.findIndex((r) => sameItem(r, item));
      // the newest already (another tab of it too: its first href is kept), or recent enough: no write per page view
      if (found === 0 || (found !== -1 && found < 3 && recent[found]?.href === item.href)) return;
      save(
        "recent",
        [{ subtitle: null, ...item, at: new Date().toISOString() }, ...recent.filter((r) => !sameItem(r, item))].slice(0, MAX_RECENT),
      );
    },
  };
}

export function hasRole(roles: readonly string[] | undefined, ...wanted: string[]): boolean {
  return !!roles && (roles.includes("admin") || wanted.some((r) => roles.includes(r)));
}
